/*
  KEVO: required terms + privacy checkbox before checkout (cart drawer and cart page).
  One document-level guard, so it keeps working after Dawn re-renders the cart footer.
  The ticked state is kept for the current page only (never pre-ticked on a new page load).

  Cart page + express checkout: Shop Pay / PayPal / Apple Pay / Google Pay start checkout from the
  cart itself, without the form post that records the consent. So before those buttons are
  unlocked, the consent is written to the cart's attributes (POST /cart/update.js) and the buttons
  are revealed only once Shopify's response confirms the attribute is stored. Unticking hides
  them again and clears the attribute.
*/
(() => {
  if (window.KevoCheckoutConsent) return;
  const state = (window.KevoCheckoutConsent = { accepted: false, recorded: false, cleared: false });

  const consentFor = (form) =>
    form && form.id ? document.querySelector(`kevo-checkout-consent[data-form="${form.id}"]`) : null;

  const showError = (consent, show) => {
    const error = consent.querySelector('.kevo-consent__error');
    const input = consent.querySelector('.kevo-consent__input');
    if (error) error.hidden = !show;
    consent.classList.toggle('kevo-consent--invalid', show);
    if (input) input.toggleAttribute('aria-invalid', show);
  };

  // Express checkout buttons (cart page) are rendered hidden + inert; reveal them only while
  // the box for the same form is ticked, and hide them again if it is unticked.
  const syncGates = (formId, accepted) => {
    document.querySelectorAll(`[data-kevo-consent-gate="${formId}"]`).forEach((gate) => {
      gate.hidden = !accepted;
      gate.toggleAttribute('inert', !accepted);
    });
    document.querySelectorAll(`[data-kevo-consent-hint="${formId}"]`).forEach((hint) => {
      hint.hidden = accepted;
    });
  };

  const hasGate = (formId) => !!document.querySelector(`[data-kevo-consent-gate="${formId}"]`);
  const updateUrl = () => `${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`;
  let saveSeq = 0;

  // Writes the consent attribute (value, or '' to remove it) and resolves true only when the
  // cart Shopify returns actually carries that value.
  // Requests run one after another, so an older "clear" can never land after a newer "save".
  let queue = Promise.resolve();
  const saveAttribute = (key, value) => {
    const run = () => writeAttribute(key, value);
    const result = queue.then(run, run);
    queue = result.catch(() => {});
    return result;
  };

  const writeAttribute = async (key, value) => {
    const response = await fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [key]: value } }),
    });
    if (!response.ok) return false;
    const cart = await response.json();
    const stored = cart && cart.attributes ? cart.attributes[key] : undefined;
    return value === '' ? stored === undefined || stored === null || stored === '' : stored === value;
  };

  const setStatus = (consent, text) => {
    const status = consent.querySelector('.kevo-consent__status');
    if (!status) return;
    status.textContent = text || '';
    status.hidden = !text;
  };

  // Ticked → save + confirm → unlock express buttons. Unticked → lock at once, then clear.
  const applyConsent = async (consent, accepted) => {
    const formId = consent.dataset.form;
    const key = consent.dataset.attributeKey;
    if (!key || !hasGate(formId)) {
      syncGates(formId, accepted);
      return;
    }
    const seq = ++saveSeq;
    syncGates(formId, false);
    if (!accepted) {
      setStatus(consent, '');
      state.recorded = false;
      saveAttribute(key, '').catch(() => {});
      return;
    }
    if (state.recorded) {
      syncGates(formId, true);
      return;
    }
    setStatus(consent, consent.dataset.saving);
    consent.setAttribute('aria-busy', 'true');
    let saved = false;
    try {
      saved = await saveAttribute(key, consent.dataset.attributeValue || '');
    } catch (error) {
      saved = false;
    }
    if (seq !== saveSeq) return; // a newer tick/untick won
    consent.removeAttribute('aria-busy');
    const input = consent.querySelector('.kevo-consent__input');
    if (saved && input && input.checked) {
      state.recorded = true;
      setStatus(consent, '');
      syncGates(formId, true);
    } else {
      setStatus(consent, consent.dataset.saveError);
    }
  };

  if (!customElements.get('kevo-checkout-consent')) {
    customElements.define(
      'kevo-checkout-consent',
      class extends HTMLElement {
        connectedCallback() {
          const input = this.querySelector('.kevo-consent__input');
          if (!input) return;
          input.checked = state.accepted;
          // Not ticked on this page but the cart still carries an older consent → remove it, so
          // the order never shows a consent that was not given in this visit.
          if (!state.accepted && this.dataset.recorded === 'true' && this.dataset.attributeKey && !state.cleared) {
            state.cleared = true;
            saveAttribute(this.dataset.attributeKey, '').catch(() => {});
          }
          if (input.checked) {
            applyConsent(this, true);
          } else {
            syncGates(this.dataset.form, false);
          }
          input.addEventListener('change', () => {
            state.accepted = input.checked;
            applyConsent(this, input.checked);
            if (input.checked) showError(this, false);
          });
        }
      }
    );
  }

  const blocked = (form) => {
    const consent = consentFor(form);
    if (!consent) return null;
    const input = consent.querySelector('.kevo-consent__input');
    return input && input.checked ? null : consent;
  };

  const stop = (event, consent) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    showError(consent, true);
    const input = consent.querySelector('.kevo-consent__input');
    if (input) input.focus({ preventScroll: true });
    consent.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  // Click on a checkout button (covers browsers without SubmitEvent.submitter).
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (!button || !button.form) return;
      const consent = blocked(button.form);
      if (consent) stop(event, consent);
    },
    true
  );

  // Block the checkout submit (button name="checkout") while the box is not ticked.
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      const consent = blocked(event.target);
      if (consent) stop(event, consent);
    },
    true
  );
})();
